import React, { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from './supabase';
import MarineOfferBuilder from './MarineOfferBuilder';
import { Save, FolderOpen, Check, AlertCircle, FileText, Calendar, DollarSign, X, Upload, Download, Clipboard, Trash2, RotateCcw, AlertTriangle } from 'lucide-react';
import { downloadImportTemplate } from './utils/excelTemplates';
import { devLog } from './utils/logger';
import Tesseract from 'tesseract.js';
import * as XLSX from 'xlsx';

// Offer numbers are text. Older rows stored them as numbers, and a numeric
// 261001 must match the string '261001'.
const offerNumberText = (value) => String(value ?? '').trim();

// A saved row with created_by NULL. Distinct from null, which means no saved
// document is open, so a legacy unowned row is not treated as ours.
const UNOWNED_DOCUMENT = '';

const COPY_ROW_META_KEYS = ['id', 'created_by', 'created_at', 'updated_at', 'deleted_at'];

const offerDataWithoutRowMeta = (data) => {
  const next = { ...(data || {}) };
  COPY_ROW_META_KEYS.forEach((key) => {
    delete next[key];
  });
  return next;
};

// 261001-C1 and 261001-C12 share the base 261001. A plain number is its own base.
const copyBaseNumber = (value) => {
  const text = offerNumberText(value);
  const match = /^(.*)-C(\d+)$/.exec(text);
  if (match && match[1]) return match[1];
  return text || 'COPY';
};

const escapeRegExp = (value) => String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// LIKE/ILIKE wildcards in a document number must stay literal. PostgREST turns
// every * into % before the query, so escaping * as \* becomes a literal %.
// A single-character wildcard keeps the prefix search, and the exact comparison
// afterwards drops the extra matches.
const escapeLikePattern = (value) => String(value)
  .replace(/[\\%_]/g, (char) => `\\${char}`)
  .replace(/\*/g, '_');

// A missing or blank offerType is an OFFER. Anything else is stored as written.
const rowOfferType = (row) => {
  const value = row?.offer_data?.offerType;
  if (value == null || String(value).trim() === '') return 'OFFER';
  return String(value);
};

// PostgREST or= for OFFER plus the legacy rows whose type is null or empty.
const OFFER_TYPE_OR_FILTER = 'offer_data->>offerType.eq.OFFER,offer_data->>offerType.is.null,offer_data->>offerType.eq.""';

// offers.id is a uuid. Import used to keep "imported-<timestamp>" as the open
// document, and Postgres rejects that value in .eq('id', ...).
const SAVED_DOCUMENT_UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const isSavedDocumentUuid = (id) => SAVED_DOCUMENT_UUID.test(String(id ?? ''));

const applyVisibleTypeFilter = (query, offerType) => {
  if (offerType === 'OFFER') return query.or(OFFER_TYPE_OR_FILTER);
  return query.eq('offer_data->>offerType', offerType);
};

// A -C suffix counts only when it is all digits and fits in a safe integer.
// Anything larger is ignored so Number() cannot collapse two different suffixes
// onto the same value.
const MAX_COPY_SUFFIX_DIGITS = String(Number.MAX_SAFE_INTEGER);

const copySuffixValue = (text, base) => {
  const match = new RegExp(`^${escapeRegExp(base)}-C(\\d+)$`).exec(offerNumberText(text));
  if (!match || !/^\d+$/.test(match[1])) return null;
  const digits = match[1].replace(/^0+/, '') || '0';
  if (digits.length > MAX_COPY_SUFFIX_DIGITS.length) return null;
  if (digits.length === MAX_COPY_SUFFIX_DIGITS.length && digits > MAX_COPY_SUFFIX_DIGITS) return null;
  return Number(digits);
};

// Highest existing <base>-C<n>, plus one. A gap is left unused on purpose so two
// tabs that see the same rows do not both pick the same hole.
const nextCopyNumber = (original, rows) => {
  const base = copyBaseNumber(original);
  let highest = 0;
  (rows || []).forEach((row) => {
    const suffix = copySuffixValue(row?.offer_number, base);
    if (suffix != null && suffix > highest) highest = suffix;
  });
  const originalSuffix = copySuffixValue(original, base);
  if (originalSuffix != null && originalSuffix > highest) highest = originalSuffix;
  return `${base}-C${highest + 1}`;
};

// After the new row is inserted, one matching number is ours. More than one
// means another visible row already has it. The lookup returns numbers only.
const numberTakenByAnotherRow = (rows, currentNumber) => {
  const wanted = offerNumberText(currentNumber);
  let count = 0;
  for (let index = 0; index < (rows || []).length; index += 1) {
    if (offerNumberText(rows[index].offer_number) === wanted) count += 1;
    if (count > 1) return true;
  }
  return false;
};

const documentTypeLabel = (type) => {
  const normalized = String(type || 'OFFER').trim().toUpperCase();
  if (normalized === 'PROFORMA INVOICE') return 'proforma';
  return normalized.toLowerCase();
};

const overwriteHeading = (candidate) => {
  const oldType = candidate?.existing?.offer_data?.offerType || 'OFFER';
  const newType = candidate?.offerToSave?.offer_data?.offerType || 'OFFER';
  if (oldType === newType) return 'Are you sure you want to overwrite this file?';
  const number = offerNumberText(candidate?.existing?.offer_number);
  return `Overwrite ${documentTypeLabel(oldType)} ${number} as ${documentTypeLabel(newType)}?`;
};

const COPY_RENAME_LIMIT = 3;

const copyRetryJitter = () => new Promise((resolve) => {
  const delay = 10 + Math.floor(Math.random() * 20);
  setTimeout(resolve, delay);
});

const MarineOfferEnhanced = () => {
  const [saveStatus, setSaveStatus] = useState('idle'); // idle, saving, saved, warning, error
  const [savedOffers, setSavedOffers] = useState([]);
  const [showSavedOffers, setShowSavedOffers] = useState(false);
  const [currentOfferId, setCurrentOfferId] = useState(null);
  // Bumps when an import clears the open id, so the builder remounts on the
  // imported lines without storing a fake document id.
  const [builderRevision, setBuilderRevision] = useState(0);
  const [offerData, setOfferData] = useState(null);
  const [loadedOfferData, setLoadedOfferData] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showImportModal, setShowImportModal] = useState(false);
  const [importText, setImportText] = useState('');
  const [importLoading, setImportLoading] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const [showFinanceConfirmModal, setShowFinanceConfirmModal] = useState(false);
  const [financeCandidate, setFinanceCandidate] = useState(null);
  const [financeConfirmChecked, setFinanceConfirmChecked] = useState(false);
  const [sendingToFinanceId, setSendingToFinanceId] = useState(null);
  const [deletedOffers, setDeletedOffers] = useState([]);
  const [showDeletedOffers, setShowDeletedOffers] = useState(false);
  const [loadingDeletedOffers, setLoadingDeletedOffers] = useState(false);
  const [deleteConfirmTarget, setDeleteConfirmTarget] = useState(null);
  const [deleteProcessing, setDeleteProcessing] = useState(false);
  // Overwrite confirmation: { existing, offerToSave } when a save would replace a saved document
  const [overwriteCandidate, setOverwriteCandidate] = useState(null);
  const [overwriteNewNumber, setOverwriteNewNumber] = useState('');
  const [overwriteProcessing, setOverwriteProcessing] = useState(false);
  const [overwriteError, setOverwriteError] = useState('');
  // Copy confirmation when the open document was created by someone else.
  // { offerToSave, originalNumber }. Admins can open those documents, but only
  // the creator may update the row.
  const [copyCandidate, setCopyCandidate] = useState(null);
  const [copyProcessing, setCopyProcessing] = useState(false);
  const [copyError, setCopyError] = useState('');
  // created_by of the document currently open. null means no saved row is open.
  // UNOWNED_DOCUMENT means the row is open and created_by is NULL.
  const [openDocumentOwnerId, setOpenDocumentOwnerId] = useState(null);
  // Shown until the user dismisses it or starts another save. Not cleared on a timer.
  const [saveError, setSaveError] = useState('');
  // "Saved as copy 261001-C1" after a copy. Cleared with the Saved! timeout.
  const [saveNotice, setSaveNotice] = useState('');
  // Ignore a document-list response that started before a newer load or save.
  const loadRequestRef = useRef(0);
  // Set synchronously so a second Save click during the lookup cannot start another write.
  const saveInFlightRef = useRef(false);
  const copyInFlightRef = useRef(false);
  const lastSavedIdRef = useRef(null);
  const saveButtonRef = useRef(null);
  const overwriteDialogRef = useRef(null);
  const builderRef = useRef(null);
  const savedResetRef = useRef(null);
  // Set once offers load, so an import can tell the signed-in user's row
  // from a document they are not allowed to overwrite.
  const signedInUserIdRef = useRef(null);

  const clearSavedReset = () => {
    if (savedResetRef.current) {
      clearTimeout(savedResetRef.current);
      savedResetRef.current = null;
    }
  };

  // Errors stay until dismissed or until the next save. "Saved!" still returns to idle.
  const failSave = (message) => {
    clearSavedReset();
    setSaveStatus('error');
    setSaveError(message);
    setSaveNotice('');
  };

  // A copy that was written, but whose number still needs the user's attention.
  // The Save button stays on this warning instead of switching to "Error!".
  const warnSave = (message) => {
    clearSavedReset();
    setSaveStatus('warning');
    setSaveError(message);
    setSaveNotice('');
  };

  const noteSaved = (notice = '') => {
    clearSavedReset();
    setSaveError('');
    setSaveNotice(notice);
    setSaveStatus('saved');
    savedResetRef.current = setTimeout(() => {
      setSaveStatus((status) => (status === 'saved' ? 'idle' : status));
      setSaveNotice('');
    }, 3000);
  };

  const dismissSaveError = () => {
    clearSavedReset();
    setSaveError('');
    setSaveStatus((status) => (status === 'error' || status === 'warning' ? 'idle' : status));
  };

  // Memoized callback to prevent infinite loops
  const handleDataChange = useCallback((data) => {
    setOfferData(data);
  }, []);

  // Attach the creator's email to a list of offers (used in admin views)
  const attachCreatorEmails = async (offers) => {
    if (!offers || offers.length === 0) return offers || [];

    const creatorIds = [...new Set(offers.map((offer) => offer.created_by).filter(Boolean))];
    const creatorsMap = new Map();

    if (creatorIds.length > 0) {
      const { data: creators } = await supabase
        .from('user_roles')
        .select('id, email')
        .in('id', creatorIds);

      if (creators && creators.length > 0) {
        creators.forEach((creator) => creatorsMap.set(creator.id, creator.email));
      }

      // Fallback: at least resolve the current user's own offers
      if (creatorsMap.size === 0) {
        const { data: { user: currentUser } } = await supabase.auth.getUser();
        if (currentUser && creatorIds.includes(currentUser.id)) {
          creatorsMap.set(currentUser.id, currentUser.email);
        }
      }
    }

    return offers.map((offer) => ({
      ...offer,
      creator_email: creatorsMap.get(offer.created_by) || 'Unknown User'
    }));
  };

  // Load saved offers
  const loadSavedOffers = async () => {
    const requestId = ++loadRequestRef.current;
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    signedInUserIdRef.current = user.id;

    devLog('User:', user);

    // Get user profile to check role from database
    let role = 'employee'; // Default fallback
    
    try {
      const { data: userProfile, error: roleError } = await supabase
        .from('user_roles')
        .select('role')
        .eq('id', user.id)
        .single();

      devLog('User profile query result:', userProfile);
      devLog('User profile query error:', roleError);
      
      if (!roleError && userProfile?.role) {
        // Check if role is admin (case-insensitive)
        const dbRole = String(userProfile.role).trim().toLowerCase();
        if (dbRole === 'admin') {
          role = 'admin';
          devLog('User has admin role in database');
        }
      }
    } catch (error) {
      devLog('Role query failed, checking fallback:', error);
    }
    
    // If database check didn't work, check if user is in the hardcoded admin list
    if (role !== 'admin') {
      const userEmail = user.email?.toLowerCase() || '';
      const allowedAdmins = [
        'kerem@atlantismarinepower.com',
        'alp@fin-marine.com'
      ];
      
      if (allowedAdmins.includes(userEmail)) {
        role = 'admin';
        devLog('User identified as admin from allowed admin list');
      }
      
      devLog('Checking email:', userEmail);
      devLog('Allowed admins:', allowedAdmins);
    }
    
    devLog('Final user role determined:', role);
    setUserRole(role);

    // Build query based on user role - restore original working query structure
    // Only show offers that have NOT been (soft) deleted in the normal list.
    let query = supabase
      .from('offers')
      .select('*')
      .eq('type', 'marine')
      .eq('status', 'draft')
      .is('deleted_at', null)
      .order('created_at', { ascending: false })
      .limit(10000);

    // Only filter by user_id if NOT admin (but ensure we always show user's own offers)
    if (role !== 'admin') {
      devLog('Filtering by user ID for employee role, user.id:', user.id);
      query = query.eq('created_by', user.id);
    } else {
      devLog('Admin role: fetching ALL marine offers without user filter');
    }

    devLog('Executing query for role:', role);
    const { data, error } = await query;

    devLog('Number of offers fetched:', data?.length || 0);
    devLog('Offers fetched:', data);
    devLog('Query error:', error);

    if (error) {
      console.error('Error loading offers:', error);
      return;
    }

    if (data) {
      let baseOffers = data;

      if (role === 'admin' && data.length > 0) {
        baseOffers = await attachCreatorEmails(data);
      }

      const offerIds = baseOffers.map((offer) => offer.id).filter(Boolean);
      if (offerIds.length > 0) {
        const { data: financeRows, error: financeError } = await supabase
          .from('finance_documents')
          .select('id, source_offer_id')
          .eq('source_module', 'marine')
          .in('source_offer_id', offerIds);

        if (!financeError && financeRows) {
          const financeMap = new Map();
          financeRows.forEach((row) => {
            if (!financeMap.has(row.source_offer_id)) {
              financeMap.set(row.source_offer_id, row.id);
            }
          });

          baseOffers = baseOffers.map((offer) => ({
            ...offer,
            in_finance: financeMap.has(offer.id),
            finance_id: financeMap.get(offer.id) || null
          }));
        }
      }

      devLog('Offers with finance status:', baseOffers);
      if (requestId !== loadRequestRef.current) return;
      setSavedOffers(baseOffers);
    }
  };

  const openSendToFinanceModal = (offer) => {
    setFinanceCandidate(offer);
    setFinanceConfirmChecked(false);
    setShowFinanceConfirmModal(true);
  };

  const closeSendToFinanceModal = () => {
    setShowFinanceConfirmModal(false);
    setFinanceCandidate(null);
    setFinanceConfirmChecked(false);
  };

  const sendToFinance = async () => {
    if (!financeCandidate || !financeConfirmChecked) return;

    const offer = financeCandidate;
    const offerDetails = offer.offer_data || {};
    const offerType = String(offerDetails.offerType || 'OFFER').toUpperCase();
    const partyType = (offerType === 'PURCHASE ORDER' || offerType === 'INQUIRY') ? 'supplier' : 'customer';

    setSendingToFinanceId(offer.id);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: existingFinance, error: checkError } = await supabase
        .from('finance_documents')
        .select('id')
        .eq('source_module', 'marine')
        .eq('source_offer_id', offer.id)
        .limit(1)
        .maybeSingle();

      if (checkError) throw checkError;

      if (!existingFinance) {
        const payload = {
          created_by: user.id,
          source_module: 'marine',
          source_offer_id: offer.id,
          document_type: String(offerType).toLowerCase().replace(/\s+/g, '_'),
          party_type: partyType,
          party_name: offer.customer_name || offerDetails.customer?.name || 'Unknown',
          document_number: offer.offer_number || offerDetails.offerNumber || null,
          title: `${offerType} ${offer.offer_number || ''}`.trim(),
          currency: offer.currency || offerDetails.currency || 'USD',
          total_amount: Number(offer.total_amount || 0),
          due_date: null,
          status: 'confirmed',
          notes: null
        };

        const { error: insertError } = await supabase
          .from('finance_documents')
          .insert([payload]);

        if (insertError) throw insertError;
      }

      await loadSavedOffers();
      closeSendToFinanceModal();
    } catch (error) {
      console.error('Send to finance failed:', error);
      const msg = String(error?.message || '').toLowerCase();
      if (msg.includes('finance_documents') || msg.includes('relation') || msg.includes('does not exist')) {
        alert('Finance Tracker database tables are not created yet. Please run supabase/finance_tracker_schema.sql in Supabase SQL Editor, then try again.');
      } else {
        alert(`Failed to send document to Finance Tracker: ${error?.message || 'Unknown error'}`);
      }
    } finally {
      setSendingToFinanceId(null);
    }
  };

  const sendCurrentToFinance = () => {
    if (!currentOfferId) {
      alert('Please save this document first, then send it to Finance.');
      return;
    }

    const offer = savedOffers.find((item) => item.id === currentOfferId);
    if (!offer) {
      alert('Please open this document from Saved Offers (or save again) before sending to Finance.');
      return;
    }

    if (offer.in_finance) {
      alert('This document is already in Finance.');
      return;
    }

    openSendToFinanceModal(offer);
  };

  // Filter offers based on search term
  const filteredOffers = savedOffers.filter(offer => {
    if (!searchTerm) return true;
    
    const search = searchTerm.toLowerCase();
    const offerData = offer.offer_data || {};
    
    // Search in customer name
    if (offer.customer_name?.toLowerCase().includes(search)) return true;
    
    // Search in offer number. Legacy rows may store the number as a number.
    if (offerNumberText(offer.offer_number).toLowerCase().includes(search)) return true;
    if (offerNumberText(offerData.offerNumber).toLowerCase().includes(search)) return true;
    
    // Search in date
    const offerDate = new Date(offer.created_at).toLocaleDateString('en-GB');
    if (offerDate.includes(search)) return true;
    
    // Search in description
    if (offerData.description?.toLowerCase().includes(search)) return true;

    // Search in engine serial number
    if (offerData.engineSerialNumber?.toLowerCase().includes(search)) return true;
    
    return false;
  });

  // Build the row we persist to Supabase from the current builder state
  const buildOfferToSave = (userId, source = offerData) => {
    const multiplierEligibleTypes = ['OFFER', 'PROFORMA INVOICE', 'INVOICE', 'COMMERCIAL INVOICE'];
    const isMultiplierApplicable = multiplierEligibleTypes.includes(source.offerType);
    const multiplierPercent = isMultiplierApplicable ? Math.max(0, Number(source.priceMultiplierPercent || 0)) : 0;
    const multiplierFactor = 1 + (multiplierPercent / 100);
    const totalAmount = source.items?.reduce((total, item) => total + ((item.totalPrice || 0) * multiplierFactor), 0) || 0;
    const offerNumber = offerNumberText(source.offerNumber) || `OFFER-${Date.now()}`;

    return {
      type: 'marine',
      offer_number: offerNumber,
      customer_name: source.customer?.name || 'Unknown Customer',
      offer_data: source.offerNumber === offerNumber ? source : { ...source, offerNumber },
      total_amount: totalAmount,
      currency: source.currency || 'USD',
      status: 'draft',
      created_by: userId
    };
  };

  // Same number and document type, owned by this user, not deleted.
  // An open document is the target when that row itself is the match, so a
  // newer packing list (or any other row) that only shares the number is not.
  const findExistingOfferByNumber = async (offerNumber, userId, offerType, openId) => {
    const columns = 'id, offer_number, customer_name, offer_data, created_at';
    const numberText = offerNumberText(offerNumber);

    if (isSavedDocumentUuid(openId)) {
      const { data: openRow, error: openError } = await supabase
        .from('offers')
        .select(columns)
        .eq('id', openId)
        .eq('offer_number', numberText)
        .eq('created_by', userId)
        .eq('type', 'marine')
        .is('deleted_at', null)
        .maybeSingle();

      if (openError) throw openError;
      // The open row is the save target even when only its type changed.
      // A different number does not match this query, so that case still
      // falls through to the type-and-number lookup below.
      if (openRow) return openRow;
    }

    let query = supabase
      .from('offers')
      .select(columns)
      .eq('offer_number', numberText)
      .eq('created_by', userId)
      .eq('type', 'marine')
      .is('deleted_at', null)
      .order('created_at', { ascending: false })
      .limit(1);
    // A legacy row with a null or empty type is an OFFER. created_by is still
    // required, so a NULL-creator row is not overwritten from here.
    query = applyVisibleTypeFilter(query, offerType);
    const { data, error } = await query.maybeSingle();

    if (error) throw error;
    return data || null;
  };

  // Another of this user's non-deleted rows with the same number and the type
  // we are about to write. Used when a type change would otherwise update the
  // open row and leave two documents with that number.
  const findOtherOwnedRowByType = async (offerNumber, userId, offerType) => {
    let query = supabase
      .from('offers')
      .select('id, offer_number, customer_name, offer_data, created_at')
      .eq('offer_number', offerNumberText(offerNumber))
      .eq('created_by', userId)
      .eq('type', 'marine')
      .is('deleted_at', null)
      .order('created_at', { ascending: false })
      .limit(1);
    query = applyVisibleTypeFilter(query, offerType);
    const { data, error } = await query.maybeSingle();
    if (error) throw error;
    return data || null;
  };

  const typeChangeClashMessage = (previousType, nextType, offerNumber) => (
    `A ${documentTypeLabel(nextType)} numbered ${offerNumberText(offerNumber)} already exists. This ${documentTypeLabel(previousType)} was not saved.`
  );

  // Keep the open editor and the in-memory list on the payload that was just written.
  // The builder remounts whenever currentOfferId changes and only reads initial
  // state from loadedOfferData, so that snapshot has to be the saved document
  // in the same render as the id change. Otherwise the screen falls back to
  // whatever was loaded before the edits.
  const showSavedDocument = (offerToSave, savedId) => {
    const savedOfferData = offerToSave.offer_data;
    setLoadedOfferData(savedOfferData);
    setOfferData(savedOfferData);
    setCurrentOfferId(savedId);
    setOpenDocumentOwnerId(offerToSave.created_by || null);
    setSavedOffers((current) => {
      const previous = current.find((row) => row.id === savedId);
      const row = {
        ...(previous || {}),
        id: savedId,
        type: offerToSave.type,
        offer_number: offerToSave.offer_number,
        customer_name: offerToSave.customer_name,
        offer_data: savedOfferData,
        total_amount: offerToSave.total_amount,
        currency: offerToSave.currency,
        status: offerToSave.status,
        created_by: offerToSave.created_by,
        deleted_at: null,
        created_at: previous?.created_at || new Date().toISOString()
      };
      if (previous) {
        return current.map((item) => (item.id === savedId ? row : item));
      }
      return [row, ...current];
    });
  };

  const UPDATE_BLOCKED_MESSAGE = 'This document could not be updated. It may have been deleted, or you no longer have permission to change it.';

  // Save never deletes a row and never writes a soft-delete flag. An update
  // names one id and the current user. Zero matching rows is an error, not an insert.
  const payloadWithoutDeleteFlags = (payload) => {
    const next = { ...(payload || {}) };
    delete next.deleted_at;
    delete next.deleted;
    if (next.offer_data && typeof next.offer_data === 'object') {
      const offerData = { ...next.offer_data };
      delete offerData.deleted_at;
      delete offerData.deleted;
      next.offer_data = offerData;
    }
    return next;
  };

  const updateOwnedOffer = async (rowId, ownerId, payload) => {
    if (!rowId || ownerId == null || ownerId === '') {
      return { data: [], error: null };
    }
    return supabase
      .from('offers')
      .update(payloadWithoutDeleteFlags(payload))
      .eq('id', rowId)
      .eq('created_by', ownerId)
      .select('id');
  };

  // Persist the offer (update when existingId is provided, insert otherwise).
  // Returns true, or an error message that should stay on screen.
  // deferDisplay leaves the editor alone so a copy can repair its number first.
  // existingId is only the open row, or the copy row this save just inserted.
  const performSave = async (offerToSave, existingId, { deferDisplay = false } = {}) => {
    setSaveStatus('saving');
    try {
      let savedId = existingId;
      if (existingId) {
        devLog('Updating existing offer with ID:', existingId);
        const { data, error } = await updateOwnedOffer(existingId, offerToSave.created_by, offerToSave);

        if (error) {
          console.error('Update error:', error);
          throw error;
        }
        if (!Array.isArray(data) || data.length === 0) {
          failSave(UPDATE_BLOCKED_MESSAGE);
          return UPDATE_BLOCKED_MESSAGE;
        }
      } else {
        devLog('Creating new offer');
        const { data, error } = await supabase
          .from('offers')
          .insert([payloadWithoutDeleteFlags(offerToSave)])
          .select();

        if (error) {
          console.error('Insert error:', error);
          throw error;
        }
        savedId = data?.[0]?.id || null;
      }

      lastSavedIdRef.current = savedId;
      if (!deferDisplay) {
        if (savedId) showSavedDocument(offerToSave, savedId);
        noteSaved();
        loadSavedOffers();
      }
      return true;
    } catch (error) {
      console.error('Save error:', error);
      const message = 'Could not save this document. Please try again.';
      failSave(message);
      return message;
    }
  };

  // Non-deleted rows of this document type, whose number starts with the copy
  // base, that the signed-in user can see. Admins see every row. Everyone else
  // sees their own, matching the list. Only the number is read. PostgREST
  // returns at most 1000 rows per request, and an unordered range can skip or
  // repeat rows, so each page is ordered by id. The scan stops after 50 pages.
  const COPY_NUMBER_PAGE_SIZE = 1000;
  const COPY_NUMBER_PAGE_CAP = 50;
  const listVisibleOfferRows = async (userId, offerType, originalNumber) => {
    const pattern = `${escapeLikePattern(copyBaseNumber(originalNumber))}%`;
    const rows = [];
    let from = 0;
    let page = [];
    let pages = 0;
    do {
      let query = supabase
        .from('offers')
        .select('offer_number')
        .eq('type', 'marine')
        .is('deleted_at', null)
        .ilike('offer_number', pattern)
        .order('id', { ascending: true });
      query = applyVisibleTypeFilter(query, offerType);
      if (userRole !== 'admin') {
        query = query.eq('created_by', userId);
      }
      const { data, error } = await query.range(from, from + COPY_NUMBER_PAGE_SIZE - 1);
      if (error) throw error;
      page = data || [];
      for (let index = 0; index < page.length; index += 1) {
        rows.push(page[index]);
      }
      pages += 1;
      from += COPY_NUMBER_PAGE_SIZE;
      if (page.length === COPY_NUMBER_PAGE_SIZE && pages >= COPY_NUMBER_PAGE_CAP) {
        const truncated = new Error('copy-number-scan-truncated');
        truncated.code = 'COPY_SCAN_TRUNCATED';
        throw truncated;
      }
    } while (page.length === COPY_NUMBER_PAGE_SIZE);
    return rows;
  };

  // Save offer
  const saveOffer = async () => {
    if (saveInFlightRef.current || overwriteCandidate || copyCandidate) return;
    saveInFlightRef.current = true;
    clearSavedReset();
    setSaveError('');
    setSaveNotice('');
    setSaveStatus('saving');
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        console.error('No user logged in');
        failSave('You need to be signed in to save.');
        return;
      }
      signedInUserIdRef.current = user.id;

      const source = builderRef.current?.getOffer?.() || null;
      if (!source) {
        console.error('No offer data to save');
        failSave('There is no document to save yet.');
        return;
      }

      const offerToSave = buildOfferToSave(user.id, source);
      devLog('Saving offer:', offerToSave);

      // Only the creator can overwrite. Another user's document, and a legacy
      // row whose created_by is NULL, is never updated and never inserted
      // under the same number. Import clears the open id so the builder can
      // remount, and leaves this owner in place so the copy path still runs.
      const savedRowIsNotMine = openDocumentOwnerId != null
        && openDocumentOwnerId !== user.id;
      if (savedRowIsNotMine) {
        const originalNumber = offerNumberText(loadedOfferData?.offerNumber || offerToSave.offer_number);
        let previewNumber = null;
        try {
          const visible = await listVisibleOfferRows(
            user.id,
            source.offerType || 'OFFER',
            originalNumber
          );
          previewNumber = nextCopyNumber(originalNumber, visible);
        } catch (lookupError) {
          console.error('Copy number preview error:', lookupError);
        }
        setCopyError('');
        setCopyCandidate({ offerToSave, originalNumber, previewNumber });
        setSaveStatus('idle');
        return;
      }

      const offerType = source.offerType || 'OFFER';
      const existing = await findExistingOfferByNumber(
        offerToSave.offer_number,
        user.id,
        offerType,
        currentOfferId
      );

      if (existing) {
        const previousType = rowOfferType(existing);
        // Changing only the type still updates this row, unless another of this
        // user's documents already has the new type and the same number.
        if (previousType !== offerType) {
          const clash = await findOtherOwnedRowByType(offerToSave.offer_number, user.id, offerType);
          if (clash && clash.id !== existing.id) {
            failSave(typeChangeClashMessage(previousType, offerType, offerToSave.offer_number));
            return;
          }
        }
        // Replacing any saved document of this type and number, including the one open.
        // Back to idle before the dialog so Save is not stuck on "Saving...".
        setOverwriteNewNumber(offerToSave.offer_number || '');
        setOverwriteError('');
        setOverwriteCandidate({ existing, offerToSave });
        setSaveStatus('idle');
        return;
      }

      await performSave(offerToSave, null);
    } catch (error) {
      console.error('Save error:', error);
      failSave('Could not save this document. Please try again.');
    } finally {
      saveInFlightRef.current = false;
    }
  };

  // User confirmed: overwrite the existing saved document
  const confirmOverwrite = async () => {
    if (!overwriteCandidate || overwriteProcessing) return;
    setOverwriteProcessing(true);
    setOverwriteError('');
    setSaveError('');
    try {
      const nextType = overwriteCandidate.offerToSave.offer_data?.offerType || 'OFFER';
      const previousType = rowOfferType(overwriteCandidate.existing);
      if (previousType !== nextType) {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('No user logged in');
        const clash = await findOtherOwnedRowByType(
          overwriteCandidate.offerToSave.offer_number,
          user.id,
          nextType
        );
        if (clash && clash.id !== overwriteCandidate.existing.id) {
          const message = typeChangeClashMessage(
            previousType,
            nextType,
            overwriteCandidate.offerToSave.offer_number
          );
          setOverwriteError(message);
          failSave(message);
          return;
        }
      }
      const outcome = await performSave(overwriteCandidate.offerToSave, overwriteCandidate.existing.id);
      if (outcome === true) {
        setOverwriteCandidate(null);
      } else {
        setOverwriteError(outcome || 'Could not overwrite this file. Please try again.');
      }
    } catch (error) {
      console.error('Overwrite error:', error);
      const message = 'Could not overwrite this file. Please try again.';
      setOverwriteError(message);
      failSave(message);
    } finally {
      setOverwriteProcessing(false);
    }
  };

  // User chose to rename instead: save under a different offer number
  const saveWithNewNumber = async () => {
    if (!overwriteCandidate || overwriteProcessing) return;

    const newNumber = overwriteNewNumber.trim();
    if (!newNumber) {
      setOverwriteError('Please enter an offer number.');
      return;
    }
    if (offerNumberText(newNumber) === offerNumberText(overwriteCandidate.existing.offer_number)) {
      setOverwriteError('This is the same number — change it, or use "Overwrite" instead.');
      return;
    }

    setOverwriteProcessing(true);
    setOverwriteError('');
    setSaveError('');
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user logged in');

      const offerType = overwriteCandidate.offerToSave.offer_data?.offerType || 'OFFER';
      const clash = await findExistingOfferByNumber(newNumber, user.id, offerType, null);
      const openSavedId = isSavedDocumentUuid(currentOfferId) ? currentOfferId : null;
      if (clash && clash.id !== openSavedId) {
        setOverwriteError(`"${newNumber}" is already used by another saved document. Pick a different number.`);
        return;
      }

      const updatedOfferData = { ...overwriteCandidate.offerToSave.offer_data, offerNumber: newNumber };
      const offerToSave = {
        ...overwriteCandidate.offerToSave,
        offer_number: newNumber,
        offer_data: updatedOfferData
      };

      // A new number is a new row. The only update allowed here is the open row.
      const outcome = await performSave(
        offerToSave,
        clash && clash.id === openSavedId ? openSavedId : null
      );
      if (outcome === true) {
        setOfferData(updatedOfferData);
        setLoadedOfferData(updatedOfferData);
        setOverwriteCandidate(null);
      } else {
        setOverwriteError(outcome || 'Something went wrong while saving. Please try again.');
      }
    } catch (error) {
      console.error('Save with new number error:', error);
      const message = 'Something went wrong while saving. Please try again.';
      failSave(message);
      setOverwriteError(message);
    } finally {
      setOverwriteProcessing(false);
    }
  };

  const cancelOverwrite = () => {
    if (overwriteProcessing) return;
    setOverwriteCandidate(null);
    setOverwriteError('');
    setOverwriteNewNumber('');
  };

  const cancelCopy = () => {
    if (copyProcessing || copyInFlightRef.current) return;
    setCopyCandidate(null);
    setCopyError('');
  };

  const confirmCopy = async () => {
    if (!copyCandidate || copyInFlightRef.current || copyProcessing) return;
    copyInFlightRef.current = true;
    setCopyProcessing(true);
    setCopyError('');
    setSaveError('');
    let insertedId = null;
    let insertedOffer = null;
    const originalNumber = copyCandidate.originalNumber;
    const typedNumber = offerNumberText(copyCandidate.offerToSave.offer_number);
    const withCopyRule = (message) => (
      typedNumber !== offerNumberText(originalNumber)
        ? `${message} (copies of another user's document always get a -C number)`
        : message
    );
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        const message = 'You need to be signed in to save.';
        failSave(message);
        setCopyError(message);
        return;
      }

      const offerType = copyCandidate.offerToSave.offer_data?.offerType || 'OFFER';
      const visible = await listVisibleOfferRows(user.id, offerType, originalNumber);
      let number = nextCopyNumber(originalNumber, visible);
      let offerDataForCopy = offerDataWithoutRowMeta({
        ...copyCandidate.offerToSave.offer_data,
        offerNumber: number
      });
      let offerToSave = {
        ...copyCandidate.offerToSave,
        offer_number: number,
        offer_data: offerDataForCopy,
        created_by: user.id
      };

      const outcome = await performSave(offerToSave, null, { deferDisplay: true });
      if (outcome !== true) {
        setCopyError(outcome || 'Could not create a copy. Please try again.');
        return;
      }
      const savedId = lastSavedIdRef.current;
      if (!savedId) {
        const message = 'Could not create a copy. Please try again.';
        failSave(message);
        setCopyError(message);
        return;
      }
      insertedId = savedId;
      insertedOffer = offerToSave;

      const openInsertedCopy = (warning) => {
        showSavedDocument(offerToSave, savedId);
        loadSavedOffers();
        setCopyCandidate(null);
        if (warning) warnSave(warning);
        else noteSaved(withCopyRule(`Saved as copy ${number}`));
      };

      // Re-check after the insert and after every rename, including the last one.
      // A failed check still leaves the new row open so Save is not clicked again.
      let renames = 0;
      while (renames <= COPY_RENAME_LIMIT) {
        let rows;
        try {
          rows = await listVisibleOfferRows(user.id, offerType, originalNumber);
        } catch (checkError) {
          console.error('Copy number check error:', checkError);
          const detail = checkError?.code === 'COPY_SCAN_TRUNCATED'
            ? 'not every saved document could be checked'
            : 'the new number could not be confirmed';
          openInsertedCopy(withCopyRule(`Saved as copy ${number}, but ${detail}`));
          return;
        }
        if (!numberTakenByAnotherRow(rows, number)) {
          openInsertedCopy('');
          return;
        }
        if (renames === COPY_RENAME_LIMIT) {
          openInsertedCopy(withCopyRule(`Saved as copy ${number}, but another document has the same number`));
          return;
        }

        await copyRetryJitter();
        let freshRows;
        try {
          freshRows = await listVisibleOfferRows(user.id, offerType, originalNumber);
        } catch (checkError) {
          console.error('Copy number check error:', checkError);
          const detail = checkError?.code === 'COPY_SCAN_TRUNCATED'
            ? 'not every saved document could be checked'
            : 'the new number could not be confirmed';
          openInsertedCopy(withCopyRule(`Saved as copy ${number}, but ${detail}`));
          return;
        }
        const next = nextCopyNumber(originalNumber, freshRows);
        offerDataForCopy = offerDataWithoutRowMeta({ ...offerDataForCopy, offerNumber: next });
        // Repair only the row this copy just inserted. Never the original.
        if (!savedId || savedId === currentOfferId) {
          openInsertedCopy(withCopyRule(`Saved as copy ${number}, but the new number could not be confirmed`));
          return;
        }
        const { data: updated, error: repairError } = await updateOwnedOffer(savedId, user.id, {
          offer_number: next,
          offer_data: offerDataForCopy
        });
        if (repairError || !Array.isArray(updated) || updated.length === 0) {
          console.error('Copy rename error:', repairError);
          openInsertedCopy(withCopyRule(`Saved as copy ${number}, but the new number could not be confirmed`));
          return;
        }
        number = next;
        offerToSave = {
          ...offerToSave,
          offer_number: number,
          offer_data: offerDataForCopy
        };
        insertedOffer = offerToSave;
        renames += 1;
      }
    } catch (error) {
      console.error('Create copy error:', error);
      if (insertedId && insertedOffer) {
        showSavedDocument(insertedOffer, insertedId);
        loadSavedOffers();
        setCopyCandidate(null);
        const detail = error?.code === 'COPY_SCAN_TRUNCATED'
          ? 'not every saved document could be checked'
          : 'the new number could not be confirmed';
        warnSave(withCopyRule(`Saved as copy ${insertedOffer.offer_number}, but ${detail}`));
        return;
      }
      const message = error?.code === 'COPY_SCAN_TRUNCATED'
        ? 'Too many saved documents share this number to check them all. The copy was not saved.'
        : 'Could not create a copy. Please try again.';
      failSave(message);
      setCopyError(message);
    } finally {
      copyInFlightRef.current = false;
      setCopyProcessing(false);
    }
  };

  const dismissSaveDialog = () => {
    if (copyCandidate) {
      cancelCopy();
      return;
    }
    cancelOverwrite();
  };
  const dismissSaveDialogRef = useRef(dismissSaveDialog);
  useEffect(() => {
    dismissSaveDialogRef.current = dismissSaveDialog;
  });

  const saveDialogOpen = Boolean(overwriteCandidate || copyCandidate);

  useEffect(() => {
    if (!saveDialogOpen) return undefined;
    const dialog = overwriteDialogRef.current;
    const saveButton = saveButtonRef.current;
    const cancelButton = dialog?.querySelector('[data-overwrite-cancel]');
    cancelButton?.focus();

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        dismissSaveDialogRef.current();
        return;
      }
      if (event.key !== 'Tab' || !dialog) return;
      const focusable = [...dialog.querySelectorAll(
        'button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])'
      )];
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      if (event.shiftKey && (active === first || !dialog.contains(active))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (active === last || !dialog.contains(active))) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      saveButton?.focus();
    };
  }, [saveDialogOpen]);

  // Load an offer
  const loadOffer = (offer) => {
    devLog('Loading offer:', offer);
    setCurrentOfferId(offer.id);
    setOpenDocumentOwnerId(offer.created_by == null ? UNOWNED_DOCUMENT : offer.created_by);
    setLoadedOfferData(offer.offer_data);
    setOfferData(offer.offer_data);
    setShowSavedOffers(false);
    setShowDeletedOffers(false);
  };

  // Load (soft) deleted offers - admin only
  const loadDeletedOffers = async () => {
    setLoadingDeletedOffers(true);
    try {
      const { data, error } = await supabase
        .from('offers')
        .select('*')
        .eq('type', 'marine')
        .not('deleted_at', 'is', null)
        .order('deleted_at', { ascending: false })
        .limit(10000);

      if (error) {
        console.error('Error loading deleted offers:', error);
        return;
      }

      const withCreators = await attachCreatorEmails(data || []);
      setDeletedOffers(withCreators);
    } finally {
      setLoadingDeletedOffers(false);
    }
  };

  const openDeletedOffers = async () => {
    setShowDeletedOffers(true);
    await loadDeletedOffers();
  };

  // Clear the currently-open offer if it was the one removed
  const clearIfCurrent = (offerId) => {
    if (offerId === currentOfferId) {
      setCurrentOfferId(null);
      setOpenDocumentOwnerId(null);
      setLoadedOfferData(null);
      setOfferData(null);
    }
  };

  // Employee soft delete - just stamp deleted_at, the row stays in the database
  const softDeleteOffer = async (offerId) => {
    if (!window.confirm('Are you sure you want to delete this offer?')) return;

    try {
      const { error } = await supabase
        .from('offers')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', offerId);

      if (error) throw error;

      loadSavedOffers();
      clearIfCurrent(offerId);
    } catch (error) {
      console.error('Soft delete error:', error);
      alert('Failed to delete offer');
    }
  };

  // Admin permanent delete - removes the row for good (used after confirmation)
  const permanentlyDeleteOffer = async (offerId) => {
    setDeleteProcessing(true);
    try {
      const { error } = await supabase
        .from('offers')
        .delete()
        .eq('id', offerId);

      if (error) throw error;

      loadSavedOffers();
      if (showDeletedOffers) loadDeletedOffers();
      clearIfCurrent(offerId);
      setDeleteConfirmTarget(null);
    } catch (error) {
      console.error('Permanent delete error:', error);
      alert('Failed to permanently delete offer');
    } finally {
      setDeleteProcessing(false);
    }
  };

  // Admin restore - clears deleted_at so the offer returns to its owner's list
  const restoreOffer = async (offerId) => {
    try {
      const { error } = await supabase
        .from('offers')
        .update({ deleted_at: null })
        .eq('id', offerId);

      if (error) throw error;

      loadDeletedOffers();
      loadSavedOffers();
    } catch (error) {
      console.error('Restore error:', error);
      alert('Failed to restore offer');
    }
  };

  // Delete button handler - role aware.
  // Employees soft delete; admins get a confirmation popup then permanent delete.
  const handleDeleteClick = (offer) => {
    if (userRole === 'admin') {
      setDeleteConfirmTarget(offer);
      return;
    }
    softDeleteOffer(offer.id);
  };

  // Handle file import (Excel or Image)
  const handleFileImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setImportLoading(true);
    
    const fileType = file.type;
    const fileName = file.name.toLowerCase();

    try {
      if (fileName.endsWith('.xlsx') || fileName.endsWith('.xls')) {
        // Handle Excel import
        const data = await file.arrayBuffer();
        const workbook = XLSX.read(data);
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
        
        parseDataToItems(jsonData);
      } else if (fileType.startsWith('image/')) {
        // Handle image import with OCR
        const result = await Tesseract.recognize(file, 'eng', {
          logger: m => devLog(m)
        });
        
        parseTextToItems(result.data.text);
      }
    } catch (error) {
      console.error('Import error:', error);
      alert('Error importing file. Please try again.');
    } finally {
      setImportLoading(false);
      e.target.value = ''; // Reset file input
    }
  };

  // Map any spelling ("New", "RECONDITIONED", "used/second hand"...) onto the
  // form's dropdown values
  const normalizeImportedCondition = (value) => {
    const text = String(value ?? '').toLowerCase();
    if (text.includes('recond')) return 'RECONDITIONED';
    if (text.includes('used') || text.includes('second')) return 'USED';
    return 'NEW';
  };

  // Imported lines replace what is on screen. The open id is cleared so a
  // fake id is never sent to offers.id and the builder remounts. The number
  // currently in the field is kept when it is not blank. On a document this
  // user does not own, a blank number falls back to that document's saved
  // number, the owner is kept, and the next save is the insert-only -C copy
  // of that number. Their own document, and a blank form, save as a new document.
  const acceptImportedItems = (items) => {
    const source = builderRef.current?.getOffer?.() || offerData || {};
    const ownerId = openDocumentOwnerId;
    const documentIsNotMine = ownerId != null && ownerId !== signedInUserIdRef.current;
    const updatedOfferData = {
      ...source,
      items
    };
    if (documentIsNotMine) {
      if (!offerNumberText(updatedOfferData.offerNumber)) {
        const savedNumber = loadedOfferData?.offerNumber;
        if (offerNumberText(savedNumber)) {
          updatedOfferData.offerNumber = savedNumber;
        }
      }
    } else {
      setOpenDocumentOwnerId(null);
    }
    setOfferData(updatedOfferData);
    setLoadedOfferData(updatedOfferData);
    setCurrentOfferId(null);
    setBuilderRevision((revision) => revision + 1);
  };

  // Parse Excel data to items
  const parseDataToItems = (data) => {
    const items = [];
    let headerRowIndex = -1;
    
    // Find header row (looking for keywords like ITEM, DESCRIPTION, QTY, etc.)
    for (let i = 0; i < Math.min(data.length, 20); i++) {
      const row = data[i];
      if (row && row.some(cell => 
        typeof cell === 'string' && 
        (cell.toUpperCase().includes('ITEM') || 
         cell.toUpperCase().includes('DESCRIPTION') ||
         cell.toUpperCase().includes('QTY'))
      )) {
        headerRowIndex = i;
        break;
      }
    }

    if (headerRowIndex === -1) return;

    // Map column indices
    const headers = data[headerRowIndex].map(h => (h || '').toString().toUpperCase());
    const colMap = {
      description: headers.findIndex(h => h.includes('DESCRIPTION')),
      partNo: headers.findIndex(h => h.includes('PART') || h.includes('P/N')),
      qty: headers.findIndex(h => h.includes('QTY') || h.includes('QUANTITY')),
      unitPrice: headers.findIndex(h => h.includes('UNIT') && h.includes('PRICE')),
      condition: headers.findIndex(h => h.includes('CONDITION')),
      remarks: headers.findIndex(h => h.includes('REMARK')),
      availability: headers.findIndex(h => h.includes('AVAILABILITY'))
    };

    // Parse items
    for (let i = headerRowIndex + 1; i < data.length; i++) {
      const row = data[i];
      if (!row || row.every(cell => !cell)) continue;

      const item = {
        id: Date.now() + i,
        description: colMap.description >= 0 ? (row[colMap.description] || '').toString() : '',
        partNo: colMap.partNo >= 0 ? (row[colMap.partNo] || '').toString() : '',
        qty: colMap.qty >= 0 ? parseFloat(row[colMap.qty]) || 1 : 1,
        unitPrice: colMap.unitPrice >= 0 ? parseFloat(row[colMap.unitPrice]) || 0 : 0,
        condition: colMap.condition >= 0 ? normalizeImportedCondition(row[colMap.condition] || 'NEW') : 'NEW',
        coo: 'none',
        cooOther: '',
        make: 'none',
        makeOther: '',
        remarks: colMap.remarks >= 0 ? (row[colMap.remarks] || '').toString() : '',
        availability: colMap.availability >= 0 ? (row[colMap.availability] || '').toString() : 'Ex stock - subject unsold'
      };

      item.totalPrice = item.qty * item.unitPrice;

      if (item.description) {
        items.push(item);
      }
    }

    // Update the offer with imported items
    if (items.length > 0) {
      acceptImportedItems(items);
      alert(`Successfully imported ${items.length} items!`);
    } else {
      alert('No items found in the file. Please check the format.');
    }
  };

  // Parse text (from OCR or paste) to items
  const parseTextToItems = (text) => {
    const lines = text.split('\n').filter(line => line.trim());
    const items = [];
    
    // Look for item patterns
    const itemPattern = /(\d+)\s+(.+?)\s+(\S+)?\s+(\d+)\s+([\d,]+\.?\d*)/;
    
    for (const line of lines) {
      const match = line.match(itemPattern);
      if (match) {
        // eslint-disable-next-line no-unused-vars
        const [_, itemNo, description, partNo, qty, price] = match;
        items.push({
          id: Date.now() + items.length,
          description: description.trim(),
          partNo: partNo || '',
          qty: parseInt(qty) || 1,
          unitPrice: parseFloat(price.replace(/,/g, '')) || 0,
          totalPrice: 0,
          condition: 'NEW',
          coo: 'none',
          cooOther: '',
          make: 'none',
          makeOther: '',
          remarks: '',
          availability: 'Ex stock - subject unsold'
        });
      }
    }

    // Calculate totals
    items.forEach(item => {
      item.totalPrice = item.qty * item.unitPrice;
    });

    if (items.length > 0) {
      acceptImportedItems(items);
      setShowImportModal(false);
      setImportText('');
      alert(`Successfully imported ${items.length} items!`);
    } else {
      alert('Could not parse items from text. Please check the format.');
    }
  };

  useEffect(() => {
    loadSavedOffers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div style={{ position: 'relative' }}>
      {/* Save/Load Bar */}
      <div style={{
        position: 'sticky',
        top: 0,
        background: 'white',
        borderBottom: '1px solid #e0e0e0',
        padding: '12px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        zIndex: 1000,
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        flexWrap: 'wrap',
        gap: '10px'
      }}>
        <div className="top-buttons-container" style={{ 
          display: 'flex', 
          gap: '8px',
          flexWrap: 'wrap',
          flex: '1 1 auto'
        }}>
          <button
            onClick={() => setShowSavedOffers(!showSavedOffers)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 12px',
              background: '#f0f0f0',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: 'clamp(12px, 3vw, 14px)',
              whiteSpace: 'nowrap',
              flex: '0 1 auto'
            }}
          >
            <FolderOpen size={16} />
            <span>{userRole === 'admin' ? `All (${savedOffers.length})` : `My Offers (${savedOffers.length})`}</span>
          </button>

          {userRole === 'admin' && (
            <button
              onClick={openDeletedOffers}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '8px 12px',
                background: '#fef2f2',
                color: '#b91c1c',
                border: '1px solid #fecaca',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: 'clamp(12px, 3vw, 14px)',
                whiteSpace: 'nowrap',
                flex: '0 1 auto'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#fee2e2'}
              onMouseLeave={(e) => e.currentTarget.style.background = '#fef2f2'}
            >
              <Trash2 size={16} />
              <span>Deleted Offers</span>
            </button>
          )}

          <button
            ref={saveButtonRef}
            type="button"
            onClick={saveOffer}
            disabled={saveStatus === 'saving'}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 12px',
              background: saveStatus === 'saved' ? '#4CAF50' :
                        saveStatus === 'warning' ? '#d97706' :
                        saveStatus === 'error' ? '#f44336' : '#2196F3',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: saveStatus === 'saving' ? 'not-allowed' : 'pointer',
              fontSize: 'clamp(12px, 3vw, 14px)',
              transition: 'background 0.3s',
              whiteSpace: 'nowrap',
              flex: '0 1 auto'
            }}
          >
            {saveStatus === 'saving' && <><Save size={16} /> Saving...</>}
            {saveStatus === 'saved' && <><Check size={16} /> Saved!</>}
            {saveStatus === 'warning' && <><AlertTriangle size={16} /> Warning</>}
            {saveStatus === 'error' && <><AlertCircle size={16} /> Error!</>}
            {saveStatus === 'idle' && <><Save size={16} /> Save</>}
          </button>

          <button
            onClick={downloadImportTemplate}
            title="Download an Excel template for the Import File button (offers, invoices, pro forma invoices, purchase orders...)"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 12px',
              background: '#6366f1',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: 'clamp(12px, 3vw, 14px)',
              transition: 'background 0.2s',
              whiteSpace: 'nowrap',
              flex: '0 1 auto'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = '#4f46e5'}
            onMouseLeave={(e) => e.currentTarget.style.background = '#6366f1'}
          >
            <Download size={16} />
            <span style={{ display: window.innerWidth > 640 ? 'inline' : 'none' }}>Template</span>
          </button>

          <button
            onClick={() => document.getElementById('import-file-input').click()}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 12px',
              background: '#9333ea',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: 'clamp(12px, 3vw, 14px)',
              transition: 'background 0.2s',
              whiteSpace: 'nowrap',
              flex: '0 1 auto'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = '#7c3aed'}
            onMouseLeave={(e) => e.currentTarget.style.background = '#9333ea'}
          >
            <Upload size={16} />
            <span style={{ display: window.innerWidth > 640 ? 'inline' : 'none' }}>Import File</span>
          </button>

          <button
            onClick={() => setShowImportModal(true)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 12px',
              background: '#10b981',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: 'clamp(12px, 3vw, 14px)',
              transition: 'background 0.2s',
              whiteSpace: 'nowrap',
              flex: '0 1 auto'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = '#059669'}
            onMouseLeave={(e) => e.currentTarget.style.background = '#10b981'}
          >
            <Clipboard size={16} />
            <span style={{ display: window.innerWidth > 640 ? 'inline' : 'none' }}>Import Text</span>
          </button>

          {/* Hidden file input */}
          <input
            id="import-file-input"
            type="file"
            aria-label="Import file"
            accept=".xlsx,.xls,.png,.jpg,.jpeg,.pdf"
            style={{ display: 'none' }}
            onChange={handleFileImport}
          />
        </div>

        <div style={{ 
          fontSize: 'clamp(11px, 2.5vw, 13px)', 
          color: '#666',
          flex: '0 1 auto',
          textAlign: 'right',
          minWidth: '0',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }}>
          {currentOfferId ? 'Editing' : 'New'}
          {offerData?.offerNumber && ` - ${offerData.offerNumber}`}
        </div>
        {saveError && (
          <div
            role="alert"
            style={{
              flex: '1 1 100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '12px',
              background: '#fef2f2',
              color: '#991b1b',
              border: '1px solid #fecaca',
              borderRadius: '8px',
              padding: '8px 12px',
              fontSize: '14px',
              lineHeight: 1.4
            }}
          >
            <span>{saveError}</span>
            <button
              type="button"
              onClick={dismissSaveError}
              style={{
                minHeight: '44px',
                minWidth: '44px',
                border: '1px solid #fecaca',
                borderRadius: '8px',
                background: 'white',
                color: '#991b1b',
                padding: '10px 16px',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: '600'
              }}
            >
              Dismiss
            </button>
          </div>
        )}
        {saveNotice && (
          <div
            role="status"
            style={{
              flex: '1 1 100%',
              background: '#ecfdf5',
              color: '#065f46',
              border: '1px solid #a7f3d0',
              borderRadius: '8px',
              padding: '8px 12px',
              fontSize: '14px',
              lineHeight: 1.4
            }}
          >
            {saveNotice}
          </div>
        )}
      </div>

      {/* Modal Overlay */}
      {showSavedOffers && (
        <div className="modal-overlay" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          backdropFilter: 'blur(4px)',
          padding: '10px'
        }}>
          {/* Modal Content */}
          <div className="modal-class smooth-scroll" style={{
            background: 'white',
            width: '100%',
            maxWidth: '900px',
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            overflow: 'hidden',
            borderRadius: '8px'
          }}>
            {/* Modal Header */}
            <div style={{
              padding: 'clamp(16px, 4vw, 32px)',
              borderBottom: '1px solid #f0f0f0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '10px'
            }}>
              <h2 style={{ 
                margin: 0, 
                fontSize: 'clamp(20px, 5vw, 28px)',
                fontWeight: '600',
                color: '#1a1a1a'
              }}>{userRole === 'admin' ? 'All Marine Offers' : 'Saved Offers'}</h2>
              <button
                onClick={() => setShowSavedOffers(false)}
                className="modal-close"
                style={{
                  background: '#f5f5f5',
                  border: 'none',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#e0e0e0';
                  e.currentTarget.style.transform = 'scale(1.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#f5f5f5';
                  e.currentTarget.style.transform = 'scale(1)';
                }}
              >
                <X size={20} color="#666" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="modal-form smooth-scroll" style={{
              flex: 1,
              overflow: 'auto',
              padding: 'clamp(16px, 4vw, 32px)'
            }}>
              {/* Search Bar */}
              <div style={{
                padding: '0 0 20px',
                borderBottom: '1px solid #f0f0f0'
              }}>
                <div style={{
                  position: 'relative'
                }}>
                  <input
                    type="text"
                    placeholder="Search by customer name, offer number, date, or engine serial number..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '12px 16px 12px 42px',
                      border: '2px solid #e5e7eb',
                      borderRadius: '12px',
                      fontSize: '14px',
                      outline: 'none',
                      transition: 'border-color 0.2s'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                    onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                  />
                  <svg
                    style={{
                      position: 'absolute',
                      left: '14px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      width: '20px',
                      height: '20px',
                      color: '#6b7280'
                    }}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  {searchTerm && (
                    <button
                      onClick={() => setSearchTerm('')}
                      style={{
                        position: 'absolute',
                        right: '12px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '4px',
                        borderRadius: '4px',
                        color: '#6b7280',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#f0f0f0';
                        e.currentTarget.style.color = '#374151';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'none';
                        e.currentTarget.style.color = '#6b7280';
                      }}
                    >
                      <X size={16} />
                    </button>
                  )}
                </div>
                {searchTerm && (
                  <p style={{
                    marginTop: '8px',
                    fontSize: '13px',
                    color: '#6b7280'
                  }}>
                    Found {filteredOffers.length} offer{filteredOffers.length !== 1 ? 's' : ''} matching "{searchTerm}"
                  </p>
                )}
              </div>

              {filteredOffers.length === 0 ? (
                <div style={{
                  textAlign: 'center',
                  padding: '60px 20px',
                  color: '#999'
                }}>
                  <FolderOpen size={64} style={{ opacity: 0.2, marginBottom: '16px' }} />
                  {searchTerm ? (
                    <>
                      <h3 style={{ margin: '0 0 8px', color: '#666', fontWeight: '500' }}>No offers found</h3>
                      <p style={{ fontSize: '15px', margin: 0 }}>Try searching with different keywords</p>
                    </>
                  ) : (
                    <>
                      <h3 style={{ margin: '0 0 8px', color: '#666', fontWeight: '500' }}>No saved offers yet</h3>
                      <p style={{ fontSize: '15px', margin: 0 }}>Create an offer and click "Save Draft" to save it</p>
                    </>
                  )}
                </div>
              ) : (
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '16px'
                }}>
                  {filteredOffers.map((offer) => {
                    const offerDetails = offer.offer_data || {};
                    const companyShort = offerDetails.company === 'fin-marine' ? 'FIN-MARINE' : 
                                        offerDetails.company === 'fin-marine-fzco' ? 'FIN-MARINE-FZCO' : 'ATLANTIS';
                    const customerClean = (offerDetails.customer?.name || 'Customer').replace(/[^a-zA-Z0-9]/g, '-');
                    const forClean = (offerDetails.description || 'Equipment').replace(/[^a-zA-Z0-9]/g, '-');
                    const docType = offerDetails.offerType === 'OFFER' ? 'OFFER' : 
                                    offerDetails.offerType === 'PROFORMA INVOICE' ? 'PROFORMA-INVOICE' :
                                    offerDetails.offerType === 'COMMERCIAL INVOICE' ? 'COMMERCIAL-INVOICE' :
                                    offerDetails.offerType === 'PURCHASE ORDER' ? 'PURCHASE-ORDER' :
                                    offerDetails.offerType === 'INQUIRY' ? 'INQUIRY' : 'INVOICE';
                    const offerFileName = `${companyShort}-For-${forClean}-${customerClean}-${offerDetails.offerNumber || offer.offer_number}-${docType}`;

                    return (
                      <div
                        key={offer.id}
                        className="offer-card"
                        style={{
                          border: '1px solid #e5e7eb',
                          borderRadius: '12px',
                          padding: 'clamp(12px, 3vw, 20px)',
                          background: '#fafafa',
                          transition: 'all 0.2s',
                          cursor: 'pointer'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.borderColor = '#3b82f6';
                          e.currentTarget.style.boxShadow = '0 10px 30px -10px rgba(59, 130, 246, 0.15)';
                          e.currentTarget.style.transform = 'translateY(-2px)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.borderColor = '#e5e7eb';
                          e.currentTarget.style.boxShadow = 'none';
                          e.currentTarget.style.transform = 'translateY(0)';
                        }}
                      >
                        {/* Offer Header */}
                        <div style={{
                          marginBottom: '16px'
                        }}>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            marginBottom: '8px',
                            flexWrap: 'wrap'
                          }}>
                            <FileText size={18} color="#3b82f6" />
                            <h3 style={{ 
                              margin: 0, 
                              fontSize: 'clamp(13px, 3.5vw, 15px)', 
                              fontWeight: '500',
                              color: '#3b82f6',
                              fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                              wordBreak: 'break-word',
                              flex: '1 1 auto'
                            }}>{offerFileName}.pdf</h3>
                          </div>
                          <p style={{ 
                            margin: 0, 
                            fontSize: 'clamp(12px, 3vw, 13px)', 
                            color: '#6b7280',
                            wordBreak: 'break-word'
                          }}>
                            {offer.offer_data?.offerType || 'OFFER'} #{offer.offer_number}
                          </p>
                        </div>

                        {/* Offer Details Grid - stack on mobile */}
                        <div className="offer-details-grid" style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                          gap: 'clamp(10px, 2vw, 16px)',
                          marginBottom: '16px',
                          fontSize: 'clamp(12px, 2.5vw, 14px)'
                        }}>
                          {/* Customer */}
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                          }}>
                            <span style={{ 
                              fontSize: '14px', 
                              color: '#6b7280',
                              fontWeight: '500',
                              minWidth: '80px'
                            }}>Customer:</span>
                            <span style={{ 
                              fontSize: '14px', 
                              color: '#111827',
                              fontWeight: '500'
                            }}>{offer.customer_name}</span>
                          </div>

                          {/* Engine Serial No (skip for Purchase Order and Packing List) */}
                          {offerDetails.offerType !== 'PURCHASE ORDER' && offerDetails.offerType !== 'PACKING LIST' && (
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px'
                            }}>
                              <span style={{ 
                                fontSize: '14px', 
                                color: '#6b7280',
                                fontWeight: '500',
                                minWidth: '80px'
                              }}>Engine SN:</span>
                              <span style={{ 
                                fontSize: '14px', 
                                color: '#111827',
                                fontWeight: '500'
                              }}>{offerDetails.engineSerialNumber || '--'}</span>
                            </div>
                          )}

                          {/* Created Date */}
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                          }}>
                            <Calendar size={16} color="#6b7280" />
                            <span style={{ 
                              fontSize: '14px', 
                              color: '#6b7280',
                              fontWeight: '500'
                            }}>Created:</span>
                            <span style={{ 
                              fontSize: '14px', 
                              color: '#111827'
                            }}>{new Date(offer.created_at).toLocaleDateString('en-GB')} at {new Date(offer.created_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}</span>
                          </div>

                          {/* Total */}
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                          }}>
                            <DollarSign size={16} color="#6b7280" />
                            <span style={{ 
                              fontSize: '14px', 
                              color: '#6b7280',
                              fontWeight: '500'
                            }}>Total:</span>
                            <span style={{ 
                              fontSize: '14px', 
                              color: '#059669',
                              fontWeight: '600'
                            }}>{offer.currency} {offer.total_amount?.toLocaleString() || '0'}</span>
                          </div>

                          {/* Status */}
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                          }}>
                            <span style={{ 
                              fontSize: '14px', 
                              color: '#6b7280',
                              fontWeight: '500'
                            }}>Status:</span>
                            <span style={{
                              background: '#fef3c7',
                              color: '#92400e',
                              padding: '4px 12px',
                              borderRadius: '6px',
                              fontSize: '12px',
                              fontWeight: '600',
                              textTransform: 'uppercase'
                            }}>{offer.status}</span>
                          </div>

                          {offer.in_finance && (
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px'
                            }}>
                              <span style={{
                                fontSize: '14px',
                                color: '#6b7280',
                                fontWeight: '500'
                              }}>Finance:</span>
                              <span style={{
                                background: '#dcfce7',
                                color: '#166534',
                                padding: '4px 12px',
                                borderRadius: '6px',
                                fontSize: '12px',
                                fontWeight: '600'
                              }}>In Finance</span>
                            </div>
                          )}

                          {/* Created By (Admin Only) */}
                          {userRole === 'admin' && (
                            <div style={{
                              gridColumn: '1 / -1',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              paddingTop: '8px',
                              borderTop: '1px solid #e5e7eb',
                              flexWrap: 'wrap'
                            }}>
                              <span style={{ 
                                fontSize: 'clamp(12px, 2.5vw, 14px)', 
                                color: '#6b7280',
                                fontWeight: '500'
                              }}>Created by:</span>
                              <span style={{ 
                                fontSize: 'clamp(12px, 2.5vw, 14px)', 
                                color: '#3b82f6',
                                fontWeight: '500',
                                wordBreak: 'break-word'
                              }}>{offer.creator_email || 'Unknown User'}</span>
                            </div>
                          )}
                        </div>

                        {/* Action Buttons - stack on mobile */}
                        <div style={{
                          display: 'flex',
                          gap: '10px',
                          marginTop: '16px',
                          flexWrap: 'wrap'
                        }}>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              loadOffer(offer);
                            }}
                            style={{
                              flex: '1 1 auto',
                              minWidth: '100px',
                              padding: 'clamp(8px, 2vw, 10px) clamp(12px, 3vw, 20px)',
                              background: '#3b82f6',
                              color: 'white',
                              border: 'none',
                              borderRadius: '8px',
                              cursor: 'pointer',
                              fontSize: 'clamp(12px, 3vw, 14px)',
                              fontWeight: '500',
                              transition: 'all 0.2s',
                              boxShadow: '0 2px 4px rgba(59, 130, 246, 0.2)'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = '#2563eb';
                              e.currentTarget.style.transform = 'translateY(-1px)';
                              e.currentTarget.style.boxShadow = '0 4px 8px rgba(59, 130, 246, 0.3)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = '#3b82f6';
                              e.currentTarget.style.transform = 'translateY(0)';
                              e.currentTarget.style.boxShadow = '0 2px 4px rgba(59, 130, 246, 0.2)';
                            }}
                          >
                            Load Offer
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              openSendToFinanceModal(offer);
                            }}
                            disabled={offer.in_finance || sendingToFinanceId === offer.id}
                            style={{
                              flex: '1 1 auto',
                              minWidth: '120px',
                              padding: 'clamp(8px, 2vw, 10px) clamp(12px, 3vw, 20px)',
                              background: offer.in_finance ? '#dcfce7' : '#0f766e',
                              color: offer.in_finance ? '#166534' : 'white',
                              border: offer.in_finance ? '1px solid #bbf7d0' : 'none',
                              borderRadius: '8px',
                              cursor: offer.in_finance ? 'not-allowed' : 'pointer',
                              fontSize: 'clamp(12px, 3vw, 14px)',
                              fontWeight: '500',
                              transition: 'all 0.2s',
                              opacity: sendingToFinanceId === offer.id ? 0.8 : 1
                            }}
                          >
                            {offer.in_finance ? 'In Finance' : (sendingToFinanceId === offer.id ? 'Sending...' : 'Send to Finance')}
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteClick(offer);
                            }}
                            style={{
                              flex: '1 1 auto',
                              minWidth: '100px',
                              padding: 'clamp(8px, 2vw, 10px) clamp(12px, 3vw, 20px)',
                              background: 'white',
                              color: '#ef4444',
                              border: '1px solid #fee2e2',
                              borderRadius: '8px',
                              cursor: 'pointer',
                              fontSize: 'clamp(12px, 3vw, 14px)',
                              fontWeight: '500',
                              transition: 'all 0.2s'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = '#fee2e2';
                              e.currentTarget.style.borderColor = '#ef4444';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = 'white';
                              e.currentTarget.style.borderColor = '#fee2e2';
                            }}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Deleted Offers Modal (admin only) */}
      {showDeletedOffers && userRole === 'admin' && (
        <div className="modal-overlay" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          backdropFilter: 'blur(4px)',
          padding: '10px'
        }}>
          <div className="modal-class smooth-scroll" style={{
            background: 'white',
            width: '100%',
            maxWidth: '900px',
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            overflow: 'hidden',
            borderRadius: '8px'
          }}>
            {/* Modal Header */}
            <div style={{
              padding: 'clamp(16px, 4vw, 32px)',
              borderBottom: '1px solid #f0f0f0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '10px'
            }}>
              <h2 style={{
                margin: 0,
                fontSize: 'clamp(20px, 5vw, 28px)',
                fontWeight: '600',
                color: '#1a1a1a',
                display: 'flex',
                alignItems: 'center',
                gap: '10px'
              }}>
                <Trash2 size={24} color="#b91c1c" />
                Deleted Offers
              </h2>
              <button
                onClick={() => setShowDeletedOffers(false)}
                className="modal-close"
                style={{
                  background: '#f5f5f5',
                  border: 'none',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#e0e0e0';
                  e.currentTarget.style.transform = 'scale(1.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#f5f5f5';
                  e.currentTarget.style.transform = 'scale(1)';
                }}
              >
                <X size={20} color="#666" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="modal-form smooth-scroll" style={{
              flex: 1,
              overflow: 'auto',
              padding: 'clamp(16px, 4vw, 32px)'
            }}>
              <p style={{
                margin: '0 0 20px',
                fontSize: '14px',
                color: '#6b7280'
              }}>
                These offers were deleted by employees. Restore one to return it to its owner's list, or permanently remove it from the database.
              </p>

              {loadingDeletedOffers ? (
                <div style={{ textAlign: 'center', padding: '60px 20px', color: '#999' }}>
                  Loading deleted offers...
                </div>
              ) : deletedOffers.length === 0 ? (
                <div style={{
                  textAlign: 'center',
                  padding: '60px 20px',
                  color: '#999'
                }}>
                  <Trash2 size={64} style={{ opacity: 0.2, marginBottom: '16px' }} />
                  <h3 style={{ margin: '0 0 8px', color: '#666', fontWeight: '500' }}>No deleted offers</h3>
                  <p style={{ fontSize: '15px', margin: 0 }}>Offers deleted by employees will appear here</p>
                </div>
              ) : (
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '16px'
                }}>
                  {deletedOffers.map((offer) => {
                    const offerDetails = offer.offer_data || {};
                    const offerName = `${offerDetails.offerType || 'OFFER'} #${offer.offer_number}`;

                    return (
                      <div
                        key={offer.id}
                        style={{
                          border: '1px solid #fecaca',
                          borderRadius: '12px',
                          padding: 'clamp(12px, 3vw, 20px)',
                          background: '#fff7f7'
                        }}
                      >
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px',
                          marginBottom: '12px',
                          flexWrap: 'wrap'
                        }}>
                          <FileText size={18} color="#b91c1c" />
                          <h3 style={{
                            margin: 0,
                            fontSize: 'clamp(13px, 3.5vw, 15px)',
                            fontWeight: '600',
                            color: '#b91c1c',
                            wordBreak: 'break-word',
                            flex: '1 1 auto'
                          }}>{offerName}</h3>
                        </div>

                        <div style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                          gap: 'clamp(8px, 2vw, 14px)',
                          marginBottom: '16px',
                          fontSize: 'clamp(12px, 2.5vw, 14px)'
                        }}>
                          <div style={{ display: 'flex', gap: '6px' }}>
                            <span style={{ color: '#6b7280', fontWeight: '500' }}>Customer:</span>
                            <span style={{ color: '#111827', fontWeight: '500', wordBreak: 'break-word' }}>{offer.customer_name || '--'}</span>
                          </div>
                          <div style={{ display: 'flex', gap: '6px' }}>
                            <span style={{ color: '#6b7280', fontWeight: '500' }}>Created by:</span>
                            <span style={{ color: '#3b82f6', fontWeight: '500', wordBreak: 'break-word' }}>{offer.creator_email || 'Unknown User'}</span>
                          </div>
                          <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                            <Calendar size={15} color="#6b7280" />
                            <span style={{ color: '#6b7280', fontWeight: '500' }}>Created:</span>
                            <span style={{ color: '#111827' }}>{new Date(offer.created_at).toLocaleDateString('en-GB')}</span>
                          </div>
                          <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                            <Trash2 size={15} color="#b91c1c" />
                            <span style={{ color: '#6b7280', fontWeight: '500' }}>Deleted:</span>
                            <span style={{ color: '#b91c1c', fontWeight: '500' }}>
                              {offer.deleted_at
                                ? `${new Date(offer.deleted_at).toLocaleDateString('en-GB')} at ${new Date(offer.deleted_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`
                                : '--'}
                            </span>
                          </div>
                        </div>

                        <div style={{
                          display: 'flex',
                          gap: '10px',
                          flexWrap: 'wrap'
                        }}>
                          <button
                            onClick={() => loadOffer(offer)}
                            style={{
                              flex: '1 1 auto',
                              minWidth: '120px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '6px',
                              padding: 'clamp(8px, 2vw, 10px) clamp(12px, 3vw, 20px)',
                              background: '#3b82f6',
                              color: 'white',
                              border: 'none',
                              borderRadius: '8px',
                              cursor: 'pointer',
                              fontSize: 'clamp(12px, 3vw, 14px)',
                              fontWeight: '500',
                              transition: 'all 0.2s'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.background = '#2563eb'}
                            onMouseLeave={(e) => e.currentTarget.style.background = '#3b82f6'}
                          >
                            <FolderOpen size={16} />
                            Load Offer
                          </button>
                          <button
                            onClick={() => restoreOffer(offer.id)}
                            style={{
                              flex: '1 1 auto',
                              minWidth: '120px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '6px',
                              padding: 'clamp(8px, 2vw, 10px) clamp(12px, 3vw, 20px)',
                              background: '#10b981',
                              color: 'white',
                              border: 'none',
                              borderRadius: '8px',
                              cursor: 'pointer',
                              fontSize: 'clamp(12px, 3vw, 14px)',
                              fontWeight: '500',
                              transition: 'all 0.2s'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.background = '#059669'}
                            onMouseLeave={(e) => e.currentTarget.style.background = '#10b981'}
                          >
                            <RotateCcw size={16} />
                            Restore
                          </button>
                          <button
                            onClick={() => setDeleteConfirmTarget(offer)}
                            style={{
                              flex: '1 1 auto',
                              minWidth: '160px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '6px',
                              padding: 'clamp(8px, 2vw, 10px) clamp(12px, 3vw, 20px)',
                              background: '#ef4444',
                              color: 'white',
                              border: 'none',
                              borderRadius: '8px',
                              cursor: 'pointer',
                              fontSize: 'clamp(12px, 3vw, 14px)',
                              fontWeight: '500',
                              transition: 'all 0.2s'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.background = '#dc2626'}
                            onMouseLeave={(e) => e.currentTarget.style.background = '#ef4444'}
                          >
                            <Trash2 size={16} />
                            Delete permanently
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Permanent Delete Confirmation Popup (admin only) */}
      {deleteConfirmTarget && (
        <div className="modal-overlay" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10060,
          padding: '10px'
        }}>
          <div className="modal-class" style={{
            background: 'white',
            width: '90%',
            maxWidth: '480px',
            borderRadius: '12px',
            padding: '24px',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              marginBottom: '12px'
            }}>
              <div style={{
                width: '44px',
                height: '44px',
                borderRadius: '50%',
                background: '#fee2e2',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}>
                <AlertTriangle size={24} color="#dc2626" />
              </div>
              <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600', color: '#1a1a1a' }}>
                Delete permanently?
              </h2>
            </div>

            <p style={{ margin: '0 0 8px', color: '#374151', fontSize: '14px', lineHeight: 1.5 }}>
              You are about to permanently delete{' '}
              <strong>
                {(deleteConfirmTarget.offer_data?.offerType || 'OFFER')} #{deleteConfirmTarget.offer_number}
              </strong>
              {deleteConfirmTarget.customer_name ? ` for ${deleteConfirmTarget.customer_name}` : ''}.
            </p>
            <p style={{ margin: '0 0 20px', color: '#b91c1c', fontSize: '14px', fontWeight: '500' }}>
              This action is permanent and cannot be undone.
            </p>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button
                onClick={() => setDeleteConfirmTarget(null)}
                disabled={deleteProcessing}
                style={{
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  background: 'white',
                  padding: '10px 16px',
                  cursor: deleteProcessing ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => permanentlyDeleteOffer(deleteConfirmTarget.id)}
                disabled={deleteProcessing}
                style={{
                  border: 'none',
                  borderRadius: '8px',
                  background: '#ef4444',
                  color: 'white',
                  padding: '10px 16px',
                  cursor: deleteProcessing ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  opacity: deleteProcessing ? 0.6 : 1
                }}
              >
                {deleteProcessing ? 'Deleting...' : 'Delete permanently'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Overwrite Confirmation Popup. Not using modal-class: that class goes
          full-screen under 768px, and this dialog should look the same on
          phones and desktops. */}
      {overwriteCandidate && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10060,
          padding: '16px',
          boxSizing: 'border-box'
        }}>
          <div
            ref={overwriteDialogRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="overwrite-dialog-title"
            style={{
              background: 'white',
              width: '100%',
              maxWidth: '420px',
              borderRadius: '12px',
              padding: '20px',
              boxSizing: 'border-box',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
            }}
          >
            <h2
              id="overwrite-dialog-title"
              style={{
                margin: '0 0 12px',
                fontSize: '18px',
                lineHeight: 1.35,
                fontWeight: '600',
                color: '#1a1a1a'
              }}
            >
              {overwriteHeading(overwriteCandidate)}
            </h2>
            <p style={{ margin: '0 0 16px', color: '#374151', fontSize: '14px', lineHeight: 1.5 }}>
              You already have a saved{' '}
              <strong>
                {(overwriteCandidate.existing.offer_data?.offerType || 'OFFER')} #{overwriteCandidate.existing.offer_number}
              </strong>
              {overwriteCandidate.existing.customer_name ? ` for ${overwriteCandidate.existing.customer_name}` : ''}
              {overwriteCandidate.existing.created_at
                ? ` (saved ${new Date(overwriteCandidate.existing.created_at).toLocaleDateString('en-GB')})`
                : ''}.
              {' '}Saving now will replace that document.
            </p>
            {overwriteError && (
              <p style={{ margin: '0 0 12px', color: '#dc2626', fontSize: '14px', lineHeight: 1.4 }}>
                {overwriteError}
              </p>
            )}
            <label
              htmlFor="overwrite-new-number"
              style={{ display: 'block', margin: '0 0 6px', fontSize: '14px', fontWeight: '600', color: '#374151' }}
            >
              Or save it under a different number instead
            </label>
            <input
              id="overwrite-new-number"
              type="text"
              value={overwriteNewNumber}
              onChange={(event) => {
                setOverwriteNewNumber(event.target.value);
                setOverwriteError('');
              }}
              disabled={overwriteProcessing}
              style={{
                width: '100%',
                minHeight: '44px',
                marginBottom: '12px',
                padding: '10px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                fontSize: '16px',
                boxSizing: 'border-box'
              }}
            />
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <button
                type="button"
                onClick={confirmOverwrite}
                disabled={overwriteProcessing}
                style={{
                  minHeight: '44px',
                  width: '100%',
                  border: 'none',
                  borderRadius: '8px',
                  background: '#d97706',
                  color: 'white',
                  padding: '12px 16px',
                  cursor: overwriteProcessing ? 'not-allowed' : 'pointer',
                  fontSize: '16px',
                  fontWeight: '600',
                  opacity: overwriteProcessing ? 0.6 : 1
                }}
              >
                {overwriteProcessing ? 'Saving...' : 'Overwrite'}
              </button>
              <button
                type="button"
                onClick={saveWithNewNumber}
                disabled={overwriteProcessing || !overwriteNewNumber.trim() || offerNumberText(overwriteNewNumber) === offerNumberText(overwriteCandidate.existing.offer_number)}
                style={{
                  minHeight: '44px',
                  width: '100%',
                  border: 'none',
                  borderRadius: '8px',
                  background: '#2563eb',
                  color: 'white',
                  padding: '12px 16px',
                  cursor: (overwriteProcessing || !overwriteNewNumber.trim() || offerNumberText(overwriteNewNumber) === offerNumberText(overwriteCandidate.existing.offer_number)) ? 'not-allowed' : 'pointer',
                  fontSize: '16px',
                  fontWeight: '600',
                  opacity: (overwriteProcessing || !overwriteNewNumber.trim() || offerNumberText(overwriteNewNumber) === offerNumberText(overwriteCandidate.existing.offer_number)) ? 0.5 : 1
                }}
              >
                {overwriteProcessing ? 'Saving...' : 'Save with new number'}
              </button>
              <button
                type="button"
                data-overwrite-cancel
                onClick={cancelOverwrite}
                disabled={overwriteProcessing}
                style={{
                  minHeight: '44px',
                  width: '100%',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  background: 'white',
                  color: '#1a1a1a',
                  padding: '12px 16px',
                  cursor: overwriteProcessing ? 'not-allowed' : 'pointer',
                  fontSize: '16px',
                  fontWeight: '600'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {copyCandidate && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10060,
          padding: '16px',
          boxSizing: 'border-box'
        }}>
          <div
            ref={overwriteDialogRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="copy-dialog-title"
            style={{
              background: 'white',
              width: '100%',
              maxWidth: '420px',
              borderRadius: '12px',
              padding: '20px',
              boxSizing: 'border-box',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
            }}
          >
            <h2
              id="copy-dialog-title"
              style={{
                margin: '0 0 12px',
                fontSize: '18px',
                lineHeight: 1.35,
                fontWeight: '600',
                color: '#1a1a1a'
              }}
            >
              This is by another user. Create a copy instead?{' '}
              {copyCandidate.previewNumber
                ? `The copy will be saved as ${copyCandidate.previewNumber}.`
                : `The copy will be saved with a -C number (e.g. ${copyBaseNumber(copyCandidate.originalNumber)}-C1).`}
            </h2>
            <p style={{ margin: '0 0 16px', color: '#374151', fontSize: '14px', lineHeight: 1.5 }}>
              Only the person who created this document can overwrite it. A copy is a new document under your account, with its own number. The original is left as it is.
            </p>
            {copyError && (
              <p style={{ margin: '0 0 12px', color: '#dc2626', fontSize: '14px', lineHeight: 1.4 }}>
                {copyError}
              </p>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <button
                type="button"
                onClick={confirmCopy}
                disabled={copyProcessing}
                style={{
                  minHeight: '44px',
                  width: '100%',
                  border: 'none',
                  borderRadius: '8px',
                  background: '#2563eb',
                  color: 'white',
                  padding: '12px 16px',
                  cursor: copyProcessing ? 'not-allowed' : 'pointer',
                  fontSize: '16px',
                  fontWeight: '600',
                  opacity: copyProcessing ? 0.6 : 1
                }}
              >
                {copyProcessing ? 'Saving...' : 'Create copy'}
              </button>
              <button
                type="button"
                data-overwrite-cancel
                onClick={cancelCopy}
                disabled={copyProcessing}
                style={{
                  minHeight: '44px',
                  width: '100%',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  background: 'white',
                  color: '#1a1a1a',
                  padding: '12px 16px',
                  cursor: copyProcessing ? 'not-allowed' : 'pointer',
                  fontSize: '16px',
                  fontWeight: '600'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {showFinanceConfirmModal && financeCandidate && (
        <div className="modal-overlay" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10050
        }}>
          <div className="modal-class" style={{
            background: 'white',
            width: '90%',
            maxWidth: '560px',
            borderRadius: '12px',
            padding: '20px',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
          }}>
            <h2 style={{ marginTop: 0, marginBottom: '12px' }}>Send to Finance</h2>
            <div style={{ display: 'grid', gap: '8px', marginBottom: '14px', fontSize: '14px' }}>
              <div><strong>Document Type:</strong> {financeCandidate.offer_data?.offerType || 'OFFER'}</div>
              <div><strong>Document Number:</strong> {financeCandidate.offer_number || '-'}</div>
              <div><strong>Party:</strong> {financeCandidate.customer_name || financeCandidate.offer_data?.customer?.name || '-'}</div>
              <div><strong>Total:</strong> {financeCandidate.currency || 'USD'} {(Number(financeCandidate.total_amount || 0)).toLocaleString()}</div>
            </div>

            <label style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '16px' }}>
              <input
                type="checkbox"
                checked={financeConfirmChecked}
                onChange={(e) => setFinanceConfirmChecked(e.target.checked)}
              />
              <span>I confirm this order/document is finalized</span>
            </label>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button
                onClick={closeSendToFinanceModal}
                style={{
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  background: 'white',
                  padding: '10px 14px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={sendToFinance}
                disabled={!financeConfirmChecked || sendingToFinanceId === financeCandidate.id}
                style={{
                  border: 'none',
                  borderRadius: '8px',
                  background: '#0f766e',
                  color: 'white',
                  padding: '10px 14px',
                  cursor: (!financeConfirmChecked || sendingToFinanceId === financeCandidate.id) ? 'not-allowed' : 'pointer',
                  opacity: (!financeConfirmChecked || sendingToFinanceId === financeCandidate.id) ? 0.5 : 1
                }}
              >
                Confirm & Send
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Import Text Modal */}
      {showImportModal && (
        <div className="modal-overlay" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1001
        }}>
          <div className="modal-class modal-form smooth-scroll" style={{
            background: 'white',
            width: '90%',
            maxWidth: '600px',
            padding: '24px',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px'
            }}>
              <h2 style={{ margin: 0 }}>Import from Text</h2>
              <button
                onClick={() => {
                  setShowImportModal(false);
                  setImportText('');
                }}
                className="modal-close"
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '5px'
                }}
              >
                <X size={24} />
              </button>
            </div>

            <p style={{ marginBottom: '16px', color: '#666' }}>
              Paste your items list here. The system will try to extract item descriptions, quantities, and prices.
            </p>

            <textarea
              value={importText}
              onChange={(e) => setImportText(e.target.value)}
              placeholder="Paste your items here...&#10;Example:&#10;1  Crankshaft assembly  CR-12345  2  1500.00&#10;2  Piston set  PS-98765  4  250.00"
              style={{
                width: '100%',
                height: '300px',
                padding: '12px',
                border: '2px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '14px',
                fontFamily: 'monospace',
                resize: 'vertical'
              }}
            />

            <div style={{
              display: 'flex',
              gap: '12px',
              marginTop: '20px',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={() => {
                  setShowImportModal(false);
                  setImportText('');
                }}
                style={{
                  padding: '10px 24px',
                  background: '#f3f4f6',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => parseTextToItems(importText)}
                disabled={!importText.trim()}
                style={{
                  padding: '10px 24px',
                  background: importText.trim() ? '#10b981' : '#e5e7eb',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: importText.trim() ? 'pointer' : 'not-allowed'
                }}
              >
                Import Items
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Loading Overlay */}
      {importLoading && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1002
        }}>
          <div style={{
            background: 'white',
            padding: '32px',
            borderRadius: '12px',
            textAlign: 'center'
          }}>
            <div style={{
              width: '40px',
              height: '40px',
              border: '4px solid #f3f4f6',
              borderTop: '4px solid #3b82f6',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 16px'
            }} />
            <p>Processing file...</p>
          </div>
        </div>
      )}

      {/* CSS for spinner animation */}
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>

      {/* Original Marine Offer Builder with data binding */}
      <MarineOfferBuilder 
        ref={builderRef}
        key={currentOfferId || `new-${builderRevision}`}
        initialData={loadedOfferData || offerData}
        onDataChange={handleDataChange}
        onSendToFinanceClick={sendCurrentToFinance}
        canSendToFinance={Boolean(currentOfferId) && !Boolean(savedOffers.find((item) => item.id === currentOfferId)?.in_finance)}
      />
    </div>
  );
};

export default MarineOfferEnhanced; 
